import React, {useState, useEffect} from 'react';
import UserWizardBody from './UserWizardBody';
import {UserCreateSchema} from '../../../api/DBSchema/User';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiErrorCodes} from '../../../api/consts';
import {SessionDataGetSchema} from '../../../api/DBSchema/SessionData';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import SMCModal from '../../../components/core/SMCModal';
import {useTranslation} from 'react-i18next';
import {useIsMountedRef} from '../../../utils/hooks';

function UserWizard({
    isOpen,
    onRequestClose,
    onSuccess
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    const isMountedRef = useIsMountedRef();

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [isTwoStepAuthenticationEnabled, setIsTwoStepAuthenticationEnabled] = useState(false);

    const [uid, setUid] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [roleId, setRoleId] = useState('');
    const [password, setPassword] = useState('');

    const onFinish = async () => {
        const createUserRequestObj = {
            [UserCreateSchema.name]: uid,
            [UserCreateSchema.firstName]: firstName,
            [UserCreateSchema.lastName]: lastName,
            [UserCreateSchema.email]: email,
            [UserCreateSchema.role]: roleId,
            [UserCreateSchema.password]: password,
        }
        setIsLoading(true);
        Api.createObject(ObjectTypes.USER, createUserRequestObj)
        .then(() => {
            if (isMountedRef.current) {
                onSuccess(uid);
            }
        })
        .catch (e => {
            if (isMountedRef.current) {
                const errorMessage = getErrorMessage(e);
                alert(errorMessage);
            }
        })
        .finally(() => {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        });
    }

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            try {
                const sessData = await Api.getSessionData({
                    attributes: [
                        SessionDataGetSchema.availableRoles,
                        SessionDataGetSchema.isPortalTwoFactorAuthenticationEnabled
                    ]
                });
                const roles = sessData.data.availableRoles;
                const isPortalTwoFactorAuthenticationEnabled = sessData.data.isPortalTwoFactorAuthenticationEnabled;
                if (Array.isArray(roles)) {
                    setAvailableRoles(roles);
                    // Set role id to be first in list
                    setRoleId(roles[0]);
                    setIsTwoStepAuthenticationEnabled(!!isPortalTwoFactorAuthenticationEnabled);
                } else {
                    throw new Error(ApiErrorCodes.GENERAL_ERROR);
                }
            } catch (e) {
                const errorMessage = getErrorMessage(e);
                setErrorMessage(errorMessage);
            }
            finally {
                setIsLoading(false);
            }
        })();
    }, [getErrorMessage]);

    // Reset to defaults on every open
    useEffect(() => {
        if (isOpen) {
            setUid('');
            setFirstName('');
            setLastName('');
            setEmail('');
            Array.isArray(availableRoles) && setRoleId(availableRoles[0]);
            setPassword('');
        }
    }, [isOpen, availableRoles]);

    const wizardVars = {
        onRequestClose,
        onFinish,
        uid, setUid,
        firstName, setFirstName,
        lastName, setLastName,
        email, setEmail,
        roleId, setRoleId,
        password, setPassword,
        isTwoStepAuthenticationEnabled,
        availableRoles
    }

    return (
        <SMCModal
            title={t('SMP.USERWIZ.CREATE_NEW_USER')}
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            width={610}
            height={600}
            loading={isLoading}
            errorMessage={errorMessage}
        >
            <UserWizardBody wizardVars={wizardVars} />
        </SMCModal>
    );
}

export default UserWizard;